import { useAccountDetails } from '@api/hooks/useAccounts';
import { useHistory } from '@api/hooks/useHistory';
import AccountInfoCard from '@components/accountInfoCard/AccountInfoCard';
import { EmptyState, NoFilteredResults } from '@components/EmptyState';
import { NewPaymentButton } from '@components/NewPaymentButton';
import { PageContainer } from '@components/PageContainer';
import { PermissionGate } from '@components/PermissionGate';
import { toastError } from '@components/Toast/Toast';
import { useBankProfile } from '@contexts/BankProfileContext';
import type { ColumnProps } from '@gurupay/gp-ui-library';
import {
	Button,
	DropdownPositions,
	FileAddIcon,
	Loader,
	OperationIcon,
	Pagination,
	SmallSelect,
	Table,
} from '@gurupay/gp-ui-library';
import { useClearFilter } from '@hooks/useClearFilter.ts';
import { usePagination } from '@hooks/usePagination';
import { isAxiosError } from 'axios';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { Capability } from 'types/roles';

import {
	AccountDetailFilter,
	type AccountDetailFilters,
} from './components/AccountDetailFilter';
import { SingleAccountRenderers } from './components/SingleAccountRenders';

const AccountDetail: FC = () => {
	const { t } = useTranslation('', { keyPrefix: 'accountStatement' });

	const SORT_OPTIONS = [
		{
			value: 'processing_started_at-desc',
			label: t('sort.newest'),
		},
		{
			value: 'processing_started_at-asc',
			label: t('sort.oldest'),
		},
	];

	const [searchParams] = useSearchParams();
	const params = new URLSearchParams(searchParams);
	const {
		currentPage,
		setCurrentPage,
		pageSize,
		pageOptions,
		handlePageSizeChange,
		pageText,
		totalCountText,
	} = usePagination();
	const [filters, setFilters] = useState<AccountDetailFilters>({});
	const [sortBy, setSortBy] = useState(SORT_OPTIONS[0].value);
	const { selectedProfile } = useBankProfile();
	const { uuid } = useParams();
	const {
		data: accountData,
		isLoading: isAccountLoading,
		failureCount,
		isError,
		error,
	} = useAccountDetails(uuid);

	const isMobile = useMediaQuery({ query: '(max-width: 480px)' });

	const filterForm = useForm<AccountDetailFilters>();
	const handleFilterApply = (filterOptions: AccountDetailFilters) => {
		setFilters(filterOptions);
		setCurrentPage(1);
	};
	const { hasFilters, handleClear } = useClearFilter<AccountDetailFilters>(
		filterForm,
		handleFilterApply,
	);

	const { data: statements, isLoading } = useHistory(
		currentPage,
		pageSize,
		selectedProfile?.id?.toString(),
		accountData?.uuid,
		filters.dateFrom,
		filters.dateTo,
		filters.beneficiary,
		filters.paymentDescription,
		sortBy,
	);

	if (accountData?.uuid) {
		params.set('uuid', String(accountData.uuid));
	}
	if (filters.dateFrom) {
		params.set('dateFrom', filters.dateFrom);
	}
	if (filters.dateTo) {
		params.set('dateTo', filters.dateTo);
	}

	const linkToStatements = `/statements/generate${params.toString() ? `?${params.toString()}` : ''}`;

	const hasFailed = !!failureCount;

	useEffect(() => {
		if (failureCount && isAxiosError(error) && error.response?.status === 404) {
			toastError(t('errors.notFound'));
		}
	}, [hasFailed, error, t, failureCount]);

	const columns: ColumnProps[] = isMobile
		? [{ label: '', key: 'statements', align: 'left' }]
		: [
				{
					label: t('tableHeaders.date'),
					key: 'date',
					align: 'left',
				},
				{
					label: t('tableHeaders.sender'),
					key: 'sender',
					align: 'left',
				},
				{
					label: t('tableHeaders.beneficiary'),
					key: 'beneficiary',
					align: 'left',
				},
				{
					label: t('tableHeaders.docNo'),
					key: 'documentNo',
					align: 'left',
				},
				{
					label: t('tableHeaders.operation'),
					key: 'details',
					align: 'left',
				},
				{
					label: t('tableHeaders.amount'),
					key: 'amount',
				},
			];

	if (isAccountLoading) {
		return (
			<div className="flex items-center justify-center h-[calc(100vh-120px)]">
				<Loader />
			</div>
		);
	}

	if (isError) {
		return <Navigate replace to={'/accounts'} />;
	}

	return (
		<PageContainer
			backUrl="/accounts"
			title={`${t('title')} - ${accountData?.ownerName}`}
		>
			<div className="flex flex-col gap-space-24 md:gap-space-40">
				<div className="flex gap-space-8">
					<Button
						href={linkToStatements}
						className="w-max flex-row-reverse"
						icon={<FileAddIcon className="w-space-16" />}
						variant="primary-black"
					>
						{t('statements')}
					</Button>
					{(!!statements?.items.length || hasFilters || isLoading) && (
						<FormProvider {...filterForm}>
							<AccountDetailFilter onApply={handleFilterApply} />
						</FormProvider>
					)}
				</div>

				<AccountInfoCard
					account={accountData}
					accountTitle={t('accountInfo.account')}
					balanceTitle={t('accountInfo.balance')}
					currencyTitle={t('accountInfo.currency')}
				/>

				{!statements?.items.length && !isLoading ? (
					hasFilters ? (
						<NoFilteredResults onClear={handleClear} />
					) : (
						<span className="flex flex-col items-center">
							<PermissionGate
								capability={Capability.CREATE_PAYMENT}
								fallback={
									<EmptyState
										icon={<OperationIcon />}
										subTitle={t('empty.readOnly.subTitle')}
										title={t('empty.readOnly.title')}
									/>
								}
							>
								<EmptyState
									icon={<OperationIcon />}
									subTitle={t('empty.subTitle')}
									title={t('empty.title')}
								/>
								<span className="-mt-space-24 max-mobile:-mt-space-12">
									<NewPaymentButton
										className="-mt-space-24"
										openDirection="top"
										variant="primary"
									>
										{t('empty.buttonText')}
									</NewPaymentButton>
								</span>
							</PermissionGate>
						</span>
					)
				) : (
					<div>
						<div className="flex gap-space-16 justify-between">
							<Button
								href={linkToStatements}
								className="flex-row-reverse"
								icon={<FileAddIcon className="w-space-16" />}
								size="sm"
								variant="secondary"
							>
								{t('statements')}
							</Button>
							<SmallSelect onChange={setSortBy} options={SORT_OPTIONS} value={sortBy} />
						</div>
						<Table
							className="mt-space-40 max-mobile:mt-space-24"
							columns={columns}
							isLoading={isLoading}
							renders={SingleAccountRenderers}
							rows={statements?.items ?? []}
							showHeader={!isMobile}
						/>
					</div>
				)}
				{statements?.pagination && statements.pagination.count > pageSize && (
					<Pagination
						currentPage={currentPage}
						dropdownOpenPosition={DropdownPositions.TOP}
						onPageChange={setCurrentPage}
						onPageSizeChange={handlePageSizeChange}
						pageOptions={pageOptions}
						pageSize={pageSize}
						pageText={pageText}
						totalCount={statements.pagination.count}
						totalCountText={totalCountText}
					/>
				)}
			</div>
		</PageContainer>
	);
};

export default AccountDetail;
